/**
 * «Создать агроцикл» (менеджер): окно с выбором культуры и года посадки,
 * шаблоном (его номер ревизии) и превью окна агроцикла. Прежде это был
 * нативный `select` и голое поле года прямо в шапке хаба — не было понятно,
 * какой шаблон ляжет в основу и на какие даты придётся цикл.
 *
 * Шаблон не выбирают: у культуры один живой шаблон, и сервер берёт его сам
 * (`create_cycle`). Окно агроцикла — окна работ этого шаблона на выбранный год
 * (`GET /agrocycle/templates/{id}/live?year=`), от первого начала до последнего
 * конца: то же, что потом покажет страница цикла.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Field, FormActions, FormDialog, Segmented, Select, useFormDialogClose, type Choice } from '../../ui/forms';
import { LoadError, failed } from '../../ui/LoadError';
import { Skeleton } from '../../ui/Skeleton';
import { cropName } from '../../../lib/crops';
import { api, ApiError } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { COMMON } from '../../../i18n/common';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { windowLabel } from '../../lib/format';
import { agrocycleApi, type AgrocycleRow } from '../agrocycle/agrocycleApi';
import './cyclesHub.css';

const m = defineMessages({
  title: { id: 'cycle.hub.create.title', defaultMessage: 'Создать агроцикл', description: 'Заголовок окна создания агроцикла (менеджер)' },
  confirm: { id: 'cycle.hub.create.confirm', defaultMessage: 'Создать', description: 'Кнопка подтверждения в окне создания агроцикла (менеджер)' },
  crop: { id: 'cycle.hub.create.crop', defaultMessage: 'Культура', description: 'Подпись выбора культуры в окне создания агроцикла (менеджер)' },
  cropOption: {
    id: 'cycle.hub.create.cropOption',
    defaultMessage: 'шаблон: ревизия {revision}, {works, plural, one {# работа} few {# работы} many {# работ} other {# работы}}',
    description: 'Вторая строка варианта культуры в окне создания агроцикла: ревизия шаблона и число работ в нём; {revision} — номер строкой, {works} — число',
  },
  year: { id: 'cycle.hub.create.year', defaultMessage: 'Год посадки', description: 'Подпись выбора года в окне создания агроцикла (менеджер)' },
  yearsTaken: { id: 'cycle.hub.create.yearsTaken', defaultMessage: '{years}: агроцикл на этот год уже есть, выбрать нельзя.', description: 'Пояснение под выбором года в окне создания агроцикла: перечисленные годы недоступны, потому что агроцикл на них уже создан; {years} — годы через запятую' },
  template: { id: 'cycle.hub.create.template', defaultMessage: 'Шаблон', description: 'Подпись строки «шаблон» в окне создания агроцикла (менеджер)' },
  templateLine: {
    id: 'cycle.hub.create.templateLine',
    defaultMessage: '{name} · ревизия {revision} · {stages, plural, one {# этап} few {# этапа} many {# этапов} other {# этапа}}, {works, plural, one {# работа} few {# работы} many {# работ} other {# работы}}',
    description: 'Строка шаблона в окне создания агроцикла; {name} — название шаблона, {revision} — номер ревизии строкой, {stages}/{works} — числа этапов и работ',
  },
  openTemplate: { id: 'cycle.hub.create.openTemplate', defaultMessage: 'Открыть шаблон', description: 'Ссылка в окне создания агроцикла: открыть шаблон культуры' },
  window: { id: 'cycle.hub.create.window', defaultMessage: 'Окно агроцикла', description: 'Подпись строки с окном нового агроцикла в окне создания (первое начало — последний конец работ плана)' },
  windowNone: { id: 'cycle.hub.create.windowNone', defaultMessage: 'у работ шаблона нет дат', description: 'Строка окна нового агроцикла, когда у работ шаблона нет дат' },
  afterNote: {
    id: 'cycle.hub.create.afterNote',
    defaultMessage: 'Агроцикл создаётся не начатым: его план — шаблон, пока вы не начнёте цикл.',
    description: 'Пояснение в окне создания агроцикла: после создания цикл ещё не идёт',
  },
  noTemplates: { id: 'cycle.hub.create.noTemplates', defaultMessage: 'Ни у одной культуры нет шаблона: сначала создайте шаблон культуры.', description: 'Пояснение в окне создания агроцикла, когда нет ни одного шаблона' },
  templatesWhat: { id: 'cycle.hub.create.templatesWhat', defaultMessage: 'шаблоны культур', description: 'Что не загрузилось (винительный падеж, в «Не удалось загрузить …»): шаблоны культур' },
  failed: { id: 'cycle.hub.create.failed', defaultMessage: 'Не получилось — повторите.', description: 'Ошибка создания агроцикла (менеджер), когда сервер не дал своего текста' },
});

type Params = Record<string, string | number | boolean | null | undefined>;

export function CreateCycleDialog({ params, rows, onClose }: { params: Params; rows: AgrocycleRow[]; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const live = useRequest(() => api.liveTemplates(params as never), [JSON.stringify(params)]);
  const templates = live.data?.items ?? [];
  const [pickedCrop, setPickedCrop] = useState('');
  const [pickedYear, setPickedYear] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const template = templates.find((item) => item.crop_code === pickedCrop) ?? templates[0] ?? null;
  const cropCode = template?.crop_code ?? '';
  const options = useMemo<Choice[]>(
    () => templates.map((item) => ({ id: item.crop_code, name: cropName(item.crop_code, item.crop_name), description: t(m.cropOption, { revision: String(item.revision), works: item.works_count }) })),
    [templates, t],
  );

  // Три ближайших года посадки; занятые (агроцикл на этот год уже есть) недоступны.
  const thisYear = new Date().getFullYear();
  const years = [thisYear, thisYear + 1, thisYear + 2];
  const taken = (year: number) => rows.some((row) => row.crop_code === cropCode && row.anchor_year === year);
  const year = pickedYear !== null && years.includes(pickedYear) && !taken(pickedYear) ? pickedYear : (years.find((y) => !taken(y)) ?? null);

  const preview = useRequest(
    () => (template && year !== null ? api.liveTemplate(template.id, { ...params, year }) : Promise.resolve(null)),
    [template?.id, year, JSON.stringify(params)],
  );
  const span = useMemo(() => {
    let from: string | null = null;
    let to: string | null = null;
    for (const stage of preview.data?.stages ?? []) {
      for (const work of stage.works) {
        if (work.window_from && (from === null || work.window_from < from)) from = work.window_from;
        const end = work.window_to ?? work.window_from;
        if (end && (to === null || end > to)) to = end;
      }
    }
    return from && to ? { from, to } : null;
  }, [preview.data]);

  const create = async () => {
    if (busy) return;
    if (!template || year === null) {
      setError(t(m.noTemplates));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await agrocycleApi.create({ crop_code: cropCode, planting_year: year }, params);
      onClose();
      navigate(`/cycles/${encodeURIComponent(created.key)}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t(m.failed));
      setBusy(false);
    }
  };

  const takenYears = years.filter(taken);

  return (
    <FormDialog
      title={t(m.title)}
      size="sm"
      isOpen
      onClose={onClose}
      isDirty={pickedCrop !== '' || pickedYear !== null}
      error={error}
      footer={<CreateActions busy={busy} onCreate={() => void create()} />}
    >
      <div className="hub-dialog">
        {failed(live) ? (
          <LoadError compact what={t(m.templatesWhat)} error={live.error} onRetry={live.reload} />
        ) : !live.data ? (
          <Skeleton variant="block" height={200} />
        ) : templates.length === 0 ? (
          <p className="hub-muted">{t(m.noTemplates)}</p>
        ) : (
          <>
            <Select label={t(m.crop)} value={cropCode} options={options} onChange={(next) => next !== '' && setPickedCrop(next)} />
            <Field label={t(m.year)} description={takenYears.length > 0 ? t(m.yearsTaken, { years: takenYears.join(', ') }) : undefined}>
              <Segmented
                aria-label={t(m.year)}
                value={year === null ? '' : String(year)}
                onChange={(next) => setPickedYear(Number(next))}
                options={years.map((y) => ({ id: String(y), label: String(y), isDisabled: taken(y) }))}
              />
            </Field>
            <dl className="hub-dialog__facts">
              <dt>{t(m.template)}</dt>
              <dd>
                {template ? t(m.templateLine, { name: cropName(template.crop_code, template.name), revision: String(template.revision), stages: template.stages_count, works: template.works_count }) : '—'}{' '}
                {template ? <Link to={`/ops/templates?template=${template.id}`}>{t(m.openTemplate)}</Link> : null}
              </dd>
              <dt>{t(m.window)}</dt>
              <dd className="num">{failed(preview) ? '—' : !preview.data && year !== null ? '…' : span ? windowLabel(span.from, span.to) : t(m.windowNone)}</dd>
            </dl>
            <p className="hub-muted">{t(m.afterNote)}</p>
          </>
        )}
      </div>
    </FormDialog>
  );
}

function CreateActions({ busy, onCreate }: { busy: boolean; onCreate: () => void }) {
  const t = useT();
  const close = useFormDialogClose();
  return <FormActions secondary={{ label: t(COMMON.cancel), onPress: close }} primary={{ label: t(m.confirm), isPending: busy, onPress: onCreate }} />;
}
